import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { coverSrc } from '@/lib/coverCredits'

// Real client case studies (each has its own /blog/*-case-study page). Newest /
// strongest first. Keep in sync with the blog index in pages/blog/BlogPage.tsx.
const caseStudies = [
  {
    slug: 'holy-trinity-case-study',
    title: 'Moving a Church Off WordPress Without Touching Their Email',
    description:
      'A bespoke rebuild for Holy Trinity Covina: off WordPress onto a fast static site scoring 96/100 on The Full Score, with the parish email untouched through the cutover.',
    highlights: ['96/100 Full Score', 'Email untouched at cutover', 'Off WordPress to static'],
  },
  {
    slug: 'sierra-canyon-case-study',
    title: 'A Digital Photo Competition for Sierra Canyon School',
    description:
      'Replacing a paper-based photo competition with a mobile-first digital platform for submissions and judging.',
    highlights: ['Mobile-first submissions', 'Digital judging', 'Paperless workflow'],
  },
  {
    slug: 'chroma-festival-case-study',
    title: "Building Chroma Festival's Complete Digital Infrastructure from Scratch",
    description:
      'A modern festival website, streamlined application system, and custom admin panel built in 8 weeks for a successful first season launch.',
    highlights: ['8-week timeline', 'Custom admin panel', '10+ hrs/week saved'],
  },
]

export default function ResourcesPage() {
  useEffect(() => {
    document.title = 'Resources for Music Leaders | Fugue Lab'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const caseStudiesRef = useScrollReveal<HTMLDivElement>('.case-study-card', { stagger: 0.1 })
  const articlesRef = useStaggeredGrid<HTMLDivElement>('.article-card', { stagger: 0.1 })
  const downloadsRef = useStaggeredGrid<HTMLDivElement>('.download-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-16 px-4 py-16">
      {/* Header */}
      <section ref={heroRef} className="mx-auto max-w-3xl space-y-4 text-center">
        <h1 className="text-4xl font-bold md:text-5xl">Resources for Music Leaders</h1>
        <p className="text-muted-foreground text-lg">
          Free guides, case studies, and insights to help you modernize your organization&apos;s digital presence,
          streamline ticketing and applications, and grow audiences.
        </p>
      </section>

      {/* Case Studies */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Case Studies</h2>
          <p className="text-muted-foreground">
            See how we&apos;ve helped classical music organizations build modern digital infrastructure and grow
            audiences.
          </p>
        </div>

        <div ref={caseStudiesRef} className="grid gap-6 md:grid-cols-1">
          {caseStudies.map((cs) => (
            <motion.div
              key={cs.slug}
              className="case-study-card"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <Link
                to={`/blog/${cs.slug}`}
                className="group border-border hover:border-primary flex flex-col overflow-hidden rounded-lg border transition-colors md:flex-row"
              >
                <img
                  src={coverSrc(cs.slug)}
                  alt=""
                  width={1600}
                  height={1000}
                  loading="lazy"
                  className="h-48 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] md:h-auto md:w-2/5"
                />
                <div className="space-y-3 p-6 md:w-3/5">
                  <div className="text-primary text-sm font-semibold tracking-wide uppercase">Case Study</div>
                  <h3 className="text-2xl font-bold">{cs.title}</h3>
                  <p className="text-muted-foreground">{cs.description}</p>
                  <div className="text-muted-foreground flex flex-wrap gap-4 text-sm">
                    {cs.highlights.map((h) => (
                      <span key={h}>&bull; {h}</span>
                    ))}
                  </div>
                  <div className="text-primary font-semibold">Read the case study &rarr;</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Blog Posts */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Latest Articles</h2>
          <p className="text-muted-foreground">
            Practical insights on music organization websites, ticketing systems, and digital strategy.
          </p>
        </div>

        <div ref={articlesRef} className="grid gap-6 md:grid-cols-2">
          <motion.div
            className="article-card"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Link
              to="/blog/festivals-losing-applicants"
              className="group border-border hover:border-primary block h-full overflow-hidden rounded-lg border transition-colors"
            >
              <img
                src={coverSrc('festivals-losing-applicants')}
                alt=""
                width={1600}
                height={1000}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="space-y-3 p-6">
                <h3 className="text-xl font-bold">5 Ways Classical Music Festivals Lose Applicants Online</h3>
                <p className="text-muted-foreground text-sm">
                  Common website and funnel mistakes that cost festivals qualified applicants—and how to fix them.
                </p>
                <div className="text-primary text-sm font-semibold">Read article &rarr;</div>
              </div>
            </Link>
          </motion.div>

          <motion.div
            className="article-card"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Link
              to="/blog/chroma-festival-case-study"
              className="group border-border hover:border-primary block h-full overflow-hidden rounded-lg border transition-colors"
            >
              <img
                src={coverSrc('chroma-festival-case-study')}
                alt=""
                width={1600}
                height={1000}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="space-y-3 p-6">
                <div className="text-primary text-xs font-semibold tracking-wide uppercase">Case Study</div>
                <h3 className="text-xl font-bold">Building Chroma Festival&apos;s Digital Infrastructure</h3>
                <p className="text-muted-foreground text-sm">
                  Website, application system, and admin panel built from scratch in 8 weeks.
                </p>
                <div className="text-primary text-sm font-semibold">Read case study &rarr;</div>
              </div>
            </Link>
          </motion.div>
        </div>

        <div className="pt-4 text-center">
          <Link to="/blog">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              className="inline-block"
            >
              <Button variant="outline" className="cursor-pointer">
                View All Articles
              </Button>
            </motion.div>
          </Link>
        </div>
      </section>

      {/* Free Downloads (Coming Soon) */}
      <section className="space-y-8">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold">Free Downloads</h2>
          <p className="text-muted-foreground">
            Practical guides and checklists to help you improve your organization&apos;s digital presence.
          </p>
        </div>

        <div ref={downloadsRef} className="grid gap-6 md:grid-cols-3">
          <motion.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">📋</div>
            <h3 className="text-xl font-bold">Festival Digital Audit Checklist</h3>
            <p className="text-muted-foreground text-sm">
              12-point checklist to audit your festival&apos;s website, application process, and enrollment funnel.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </motion.div>

          <motion.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">📄</div>
            <h3 className="text-xl font-bold">Festival Website RFP Template</h3>
            <p className="text-muted-foreground text-sm">
              Template for requesting proposals from web developers, including budget planning and vendor questions.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </motion.div>

          <motion.div
            className="download-card border-border space-y-3 rounded-lg border p-6 opacity-75"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-4xl">🎯</div>
            <h3 className="text-xl font-bold">Application Funnel Setup Guide</h3>
            <p className="text-muted-foreground text-sm">
              Step-by-step guide to setting up enrollment tracking and optimizing your application process.
            </p>
            <div className="text-muted-foreground text-sm italic">Coming soon</div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-12 text-center">
        <div className="cta-content">
          <h2 className="text-3xl font-bold">Need Help Implementing These Strategies?</h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your organization&apos;s digital needs. We&apos;ll review
            your current setup and show you exactly how we can help.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Schedule Your Free Strategy Call &rarr;
                </Button>
              </motion.div>
            </a>
            <Link to="/packages">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  View Packages &amp; Pricing
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
